<template>
    <ReaderModal :aria-label="localizations[lang].onboarding.title || 'Onboarding'" @close-modal="closeOnboarding">
        <div ref="ReaderOnboarding" dusk="ReaderOnboarding" class="relative h-[600px] w-full">
            <div class="h-[calc(100%-80px)] w-full px-0">
                <div v-for="(step, index) in getStepsData()" :key="index" class="relative">
                    <transition name="fade">
                        <OnboardingStep v-show="onboardingSteps.position === index" :step="step" />
                    </transition>
                </div>
            </div>
            <div class="absolute bottom-0 left-0 right-0 w-full bg-theme-bg px-0">
                <div class="mx-0 w-full">
                    <div class="mb-1 inline-flex w-full items-center justify-start py-2 pl-4">
                        <button
                            v-for="step in numberOfStep + 1"
                            :key="step"
                            type="button"
                            class="flatButton mx-1"
                            @click="goToStep(step - 1)"
                        >
                            <span
                                class="flex h-6 w-6 items-center justify-center rounded-full border-2 border-theme-border bg-transparent text-xs text-theme-text transition-all duration-300 ease-in-out hover:border-theme-icon hover:text-theme-icon active:border-theme-icon active:bg-theme-icon active:text-theme-bg"
                                :class="{
                                    '!border-theme-icon !bg-theme-icon !text-theme-bg':
                                        step == onboardingSteps.position + 1,
                                }"
                            >
                                {{ step }}
                            </span>
                        </button>
                    </div>
                    <NavigationButtons
                        class="mb-0"
                        :show-back-button="showBackButton"
                        :is-last-step="onboardingSteps.position === numberOfStep"
                        :localizations="localizations"
                        :lang="lang"
                        @prev="prevStep"
                        @next="nextStep"
                    />
                </div>
            </div>
        </div>
    </ReaderModal>
</template>
<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import OnboardingStep from './steps/OnboardingStep.vue';
import onboardingData from './../../../src/js/onboardingData.js';
import Hammer from 'hammerjs';
import ReaderModal from '../ReaderModal/ReaderModal.vue';
import NavigationButtons from './NavigationButtons.vue';
export default {
    components: {
        ReaderModal,
        OnboardingStep,
        NavigationButtons,
    },
    data() {
        return {
            onboardingSteps: {
                position: 0,
                steps: [],
            },
            promoteApp: true,
            hammerInstance: null,
        };
    },
    computed: {
        ...mapState(['onboarding', 'localizations', 'lang', 'smartZoom', 'device']),
        ...mapGetters(['isEpub', 'isAudiobook', 'isPromoteAppEnabled']),
        numberOfStep() {
            return this.getStepsData().length - 1;
        },
        showPromoteAppInOnboarding() {
            if (!this.isPromoteAppEnabled) {
                return false;
            }

            try {
                return !localStorage.getItem('promoteAppHasBeenShown:v1');
            } catch (error) {
                console.error(error);
                return true;
            }
        },
        showPromoteAppEnabled() {
            return this.isPromoteAppEnabled && this.showPromoteAppInOnboarding && this.promoteApp;
        },
        showBackButton() {
            if (this.showPromoteAppEnabled) {
                return false;
            }

            if (!this.showPromoteAppInOnboarding && this.onboardingSteps.position === 0) {
                return false;
            }

            return true;
        },
    },
    mounted() {
        this.pauseKeyboardNavigation();
        document.addEventListener('keydown', this.handleKeyboardArrowKeys, false);
        this.handleSwipe();
        this.onboardingSteps.position = 0;
    },
    beforeDestroy() {
        this.resumeKeyboardNavigation();
        document.removeEventListener('keydown', this.handleKeyboardArrowKeys);

        // Destroy Hammer instance
        if (this.hammerInstance) {
            this.hammerInstance.destroy();
            this.hammerInstance = null;
        }

        // to avoid showing again when is open the onboarding manually
        localStorage.setItem('promoteAppHasBeenShown:v1', 'true');
    },
    methods: {
        ...mapMutations(['closeOnboarding', 'pauseKeyboardNavigation', 'resumeKeyboardNavigation']),
        ...mapActions(['trackOnboarding']),
        getStepsData() {
            const steps = onboardingData();
            steps.setLocalizations(this.localizations[this.lang].onboarding);
            steps.setIsPromoteAppEnabled(this.isPromoteAppEnabled);
            if (this.isAudiobook) {
                return steps.getAudiobookSteps();
            }

            if (!this.smartZoom.enabled || this.isEpub) {
                return steps.getReaderSteps().filter(step => {
                    return step.step_name !== 'smart-zoom';
                });
            }

            return steps.getReaderSteps();
        },
        nextStep(e, trigger = 'click') {
            if (this.numberOfStep === this.onboardingSteps.position && trigger === 'click')
                return this.closeOnboarding();
            if (this.numberOfStep === this.onboardingSteps.position && trigger !== 'click') return;
            this.onboardingSteps.position++;
            this.trackOnboarding({
                action: 'next',
                trigger,
                step: this.onboardingSteps.position + 1,
            });
        },
        prevStep(e, trigger = 'click') {
            if (this.onboardingSteps.position === 0) return;
            this.onboardingSteps.position--;
            this.trackOnboarding({
                action: 'previous',
                trigger,
                step: this.onboardingSteps.position + 1,
            });
        },
        goToStep(index) {
            this.onboardingSteps.position = index;
            this.trackOnboarding({
                action: 'bullet',
                trigger: 'click',
                step: this.onboardingSteps.position + 1,
            });
        },
        handleSwipe() {
            // Ensure DOM is ready before initializing Hammer
            this.$nextTick(() => {
                if (this.$refs.ReaderOnboarding && !this.hammerInstance) {
                    this.hammerInstance = new Hammer.Manager(this.$refs.ReaderOnboarding);
                    this.hammerInstance.add(new Hammer.Swipe({ direction: Hammer.DIRECTION_HORIZONTAL }));
                    this.hammerInstance.on('swipeleft', e => {
                        this.nextStep(e, 'swipe');
                    });
                    this.hammerInstance.on('swiperight', e => {
                        this.prevStep(e, 'swipe');
                    });
                }
            });
        },
        handleKeyboardArrowKeys(e) {
            if (e.key === 'ArrowLeft') {
                (e.preventDefault(), this.prevStep(e, 'keyboard'));
            }
            if (e.key === 'ArrowRight') {
                (e.preventDefault(), this.nextStep(e, 'keyboard'));
            }
            if (e.key === 'Escape') {
                (e.preventDefault(), this.closeOnboarding());
            }
        },
    },
};
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
    @apply transition-opacity duration-[350ms];
}

.fade-enter,
.fade-leave-to {
    @apply absolute z-50 opacity-0;
}

.fade-leave-to {
    @apply invisible;
}
</style>
